/**
 * Carries "why were you signed out" from the axios interceptor to the login screen.
 *
 * The redirect goes through `window.location.href`, which throws away React state and any
 * router-level payload, so the reason has to survive a full document load. sessionStorage is
 * the narrowest thing that does: it is scoped to the tab, dies with it, and never lands in the
 * URL (a query string would linger in history and in anything the user bookmarks).
 *
 * The value is an i18n KEY produced by the API (`SessionEndReasonKeys`), never a sentence and
 * never anything user-identifying — translation happens on the login screen so the message
 * follows the interface language rather than whatever it was at the moment of logout.
 *
 * Read is destructive on purpose: the reason explains one specific sign-out, so it must not
 * reappear the next time the user visits /login (AB#1735).
 */
const STORAGE_KEY = 'beetegre-session-end-reason';

/** Mirrors `SessionEndReasonKeys` on the API side; the two lists must stay in step. */
export const SessionEndReasons = {
  invalid: 'auth.session.ended.invalid',
  expired: 'auth.session.ended.expired',
  securityRevoked: 'auth.session.ended.securityRevoked',
  revokedElsewhere: 'auth.session.ended.revokedElsewhere',
} as const;

export type SessionEndReason = (typeof SessionEndReasons)[keyof typeof SessionEndReasons];

/** Keys the API is allowed to hand us. Anything else is ignored rather than rendered. */
const KNOWN_REASON_KEYS = new Set<string>(Object.values(SessionEndReasons));

export function rememberSessionEndReason(reasonKey: string | null | undefined): void {
  if (!reasonKey || !KNOWN_REASON_KEYS.has(reasonKey)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, reasonKey);
  } catch {
    // Private-mode or quota — the sign-out still has to happen, it just loses its caption.
  }
}

/**
 * Reads the stored key WITHOUT clearing it.
 *
 * Clearing on read looks tidier but loses the message: signing out first calls the auth store's
 * `logout()`, which re-renders the route guard and mounts the login screen client-side, and only
 * then does `window.location.href` replace the document. A destructive read is spent by that
 * throwaway mount, so the document the user actually sees finds nothing left (AB#1735).
 */
export function peekSessionEndReason(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value && KNOWN_REASON_KEYS.has(value) ? value : null;
  } catch {
    return null;
  }
}

/** Drops the reason once it has served its purpose, i.e. the user has signed back in. */
export function clearSessionEndReason(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: an unreadable store cannot be holding a stale reason either.
  }
}

/**
 * Pulls the reason key out of a failed refresh response. The API puts it in ProblemDetails
 * `detail`; anything unrecognised is dropped so a future backend change cannot leak a raw
 * identifier onto the login screen.
 *
 * Deliberately reads `detail` directly instead of going through `utils/extractApiError`,
 * which also exposes a raw `messageKey`. That reader answers "give me whatever reason this
 * response carries" and checks `detail` LAST, after `message` / `error` / `errorKey` /
 * `messageKey` / `errors`. Here the location is the contract: the refresh endpoint puts the
 * session-end key in `detail` and nowhere else, so a body that ever grew a `message` would
 * outvote it there, the whitelist below would reject the winner, and the caption would
 * vanish with nothing on screen to say why.
 *
 * Faz 2 (PBI #1739) merges `mapBackendErrors` and `extractApiError` into one `readApiError`.
 * This is a third reader and should be folded in deliberately then — keeping both the
 * `detail`-specific lookup and the whitelist, which are what make it safe.
 */
export function extractSessionEndReason(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && KNOWN_REASON_KEYS.has(detail) ? detail : null;
}
